/**
 * ChatPane — iter-121.29 cosmetic redesign.
 *
 * Same chat semantics (provider/dataset picker, builder toggle, structured
 * builder for SAs, multi-source binding, markdown render, tool-call cards,
 * save-to-gallery actions) — just a polished welcome screen + cleaner
 * header/input/footer per the design comp:
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ ✨ Sense AI                  🔗 Builder · 🗄 Context · 🤖 LLM │  ← header
 *   │   NATURAL LANGUAGE ANALYTICS                                │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │                                                             │
 *   │              Ask anything about your data.                  │  ← centered hero
 *   │     Sense AI reads your data, finds patterns…               │
 *   │                                                             │
 *   │     ┌──────────────────────────────────────────────┐        │
 *   │     │ Ask a question about your data…              │        │  ← large input
 *   │     │ 📎 🗄                       Shift+Enter   ➤  │        │
 *   │     └──────────────────────────────────────────────┘        │
 *   │                                                             │
 *   │     Try asking about your data                              │
 *   │     [📈 revenue] [👥 segments] [🎯 ROAS] [📊 forecast]      │  ← colored cards
 *   │                                                             │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │ 🛡 Your data is private and secure …   Learn more →         │  ← footer
 *   └─────────────────────────────────────────────────────────────┘
 */
import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Sparkles, Send, Database, Wrench, Pencil, BookmarkPlus, BookmarkCheck, ChevronDown,
    Palette, Lock,
    X, Paperclip, ShieldCheck, ExternalLink, TrendingUp, Users, Target,
    BarChart3, ShieldQuestion, AlertTriangle, Loader2,
} from "lucide-react";
import { useEntitlements } from "@/lib/use-entitlements";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import BuilderToolCard from "@/components/sense-ai/BuilderToolCard";
import StructuredView, { stripStructured } from "@/components/sense-ai/StructuredView";
import api from "@/lib/api";
import { toast } from "sonner";
import { applyDashboardTheme } from "@/lib/settings-context";


/**
 * iter-121.132 — SA-only "Apply Theme" toolbar button for the Sense AI
 * Builder. Mirrors the same affordance on the prebuilt dashboard header
 * (DashboardDetailView) so the SA can re-stamp the tenant's effective
 * Dashboard Theme without leaving the Builder pane.
 */
function ApplyThemeToolbarButton() {
    const [busy, setBusy] = React.useState(false);
    const onApply = async () => {
        setBusy(true);
        try {
            const r = await api.get("/settings/dashboard-theme");
            const cfgToApply = r?.data?.effective_config || r?.data?.config;
            if (cfgToApply && r?.data?.registry) {
                applyDashboardTheme(cfgToApply, r.data.registry);
                toast.success("Dashboard theme applied");
            } else {
                toast.info("No theme configured — open Settings → Dashboard Theme");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to apply theme");
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            onClick={onApply}
            disabled={busy}
            className="h-9 rounded-md border bg-card border-border hover:border-foreground/30 px-3 text-xs flex items-center gap-1.5 transition-colors disabled:opacity-50"
            data-testid="sense-ai-apply-theme-btn"
            title="Re-apply the tenant's Dashboard Theme to this view"
        >
            <Palette className="h-3.5 w-3.5" />
            {busy ? "Applying…" : "Apply Theme"}
        </button>
    );
}

/* -------------------------------------------------------------------------- */
/*  ProviderTriggerValue — iter-121.40                                        */
/*                                                                            */
/*  Custom render for the selected LLM in the provider Select trigger. Shows  */
/*  a compact "Platform default" pill (clickable → Settings/AI Providers)     */
/*  next to the model label when the active provider is inherited from the   */
/*  SA-managed fallback. Gives OAs visual confirmation that they're using    */
/*  shared billing — and a one-click path to configure their own key for    */
/*  billing isolation.                                                      */
/* -------------------------------------------------------------------------- */
function ProviderTriggerValue({ providers, value, disabled }) {
    const navigate = useNavigate();
    const selected = providers.find((p) => p.value === value);
    if (!selected) {
        return (
            <SelectValue placeholder={disabled ? "No provider configured" : "Select LLM"} />
        );
    }
    const isPlatformDefault = selected.source === "platform_default";
    return (
        <div className="flex items-center gap-1.5 min-w-0 w-full text-left">
            <span className="truncate" data-testid="sense-ai-provider-trigger-label">{selected.label}</span>
            {isPlatformDefault && (
                <span
                    role="button"
                    tabIndex={0}
                    onPointerDown={(e) => { e.stopPropagation(); }}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/settings?tab=llm"); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/settings?tab=llm"); } }}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-700 dark:text-amber-300 text-[9px] font-mono uppercase tracking-wide shrink-0 cursor-pointer transition-colors"
                    title="Using the platform default key configured by the Super Admin. Click to configure your own key for billing isolation."
                    data-testid="sense-ai-provider-trigger-platform-default-pill">
                    <ShieldQuestion className="h-2.5 w-2.5" /> Platform default
                </span>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  DataContextSelect — source-grouped picker (iter-121.31)                   */
/*                                                                            */
/*  End-users see SOURCES, not individual datasets — picking "Google Ads"     */
/*  auto-binds every dataset that belongs to that source. The underlying      */
/*  array of dataset_ids (the wire-format the backend understands) is still   */
/*  what flows through `onChange`; this control is purely a smarter mapping   */
/*  between source-level intent and dataset-level wiring.                     */
/*                                                                            */
/*  Sources are derived from the dataset list itself (datasets carry          */
/*  `connector_id` + already-resolved `source_name` from the backend          */
/*  response enricher in /data/datasets) — no extra API call required.       */
/*                                                                            */
/*  Canonical Models stay as their own section (SA-only) and use the          */
/*  `model:<slug>` id namespacing for downstream routing.                     */
/*                                                                            */
/*  iter-121.31b — Popover is now CONTROLLED so the input-area database       */
/*  shortcut (inside the prompt textarea) can open the same picker as the     */
/*  header chip. The shortcut button used to merely flash the header — now    */
/*  both surfaces drive identical UX via the shared `open` / `onOpenChange`   */
/*  props the ChatPane root passes in.                                       */
/* -------------------------------------------------------------------------- */
function DataContextSelect({ datasets, value, onChange, open, onOpenChange }) {
    const selected = value || [];
    const canonical = (datasets || []).filter((d) => d._canonical);
    const raw = (datasets || []).filter((d) => !d._canonical);

    // Group raw datasets by connector_id / source_id (whichever exists).
    // Per iter-121.31 spec: hide sources with 0 datasets entirely. Since
    // we're deriving sources FROM the dataset list, every group has ≥1
    // dataset by definition.
    //
    // iter-121.31a — Also hide ORPHANED sources whose backing connector
    // (or upstream source) was deleted. We detect these by the
    // `source_name` field coming back empty/null from the backend (the
    // dataset enricher leaves it blank when the parent connector can't
    // be resolved). Surfacing them as nameless rows ("(no source_name)")
    // inflated the picker count vs. the connector list and confused the
    // user. The underlying datasets become unreachable from the picker,
    // which is the correct behaviour — orphaned data belongs in Data →
    // Datasets where the SA can either re-bind or delete them.
    const sourceMap = new Map();
    for (const d of raw) {
        const key = d.connector_id || d.source_id || `unsourced:${d.id}`;
        const label = d.source_name || d.name || "Untitled source";
        // Orphan filter: any dataset with a connector_id/source_id but
        // EMPTY source_name → parent connector deleted. Skip silently.
        const isOrphan = (d.connector_id || d.source_id) && !d.source_name;
        if (isOrphan) continue;
        if (!sourceMap.has(key)) {
            sourceMap.set(key, {
                key, label, dataset_ids: [], unsourced: key.startsWith("unsourced:"),
                // Stage 3 — category + model the source resolves to (from /data/datasets).
                category: d.source_category || (d.connector_id ? null : "file"),
                model: d.resolved_model_name || null, basis: d.resolution_basis || null,
            });
        }
        sourceMap.get(key).dataset_ids.push(d.id);
    }
    const sources = Array.from(sourceMap.values());

    // A source is "selected" when ALL of its dataset_ids are in the
    // current selection. Toggle adds/removes the whole set.
    const isSourceSelected = (src) => src.dataset_ids.every((id) => selected.includes(id));
    const isSourcePartial = (src) =>
        !isSourceSelected(src) && src.dataset_ids.some((id) => selected.includes(id));

    const toggleSource = (src) => {
        if (isSourceSelected(src)) {
            // Remove all of this source's datasets
            const remove = new Set(src.dataset_ids);
            onChange(selected.filter((id) => !remove.has(id)));
        } else {
            // Add any missing dataset_ids
            const next = [...selected];
            for (const id of src.dataset_ids) if (!next.includes(id)) next.push(id);
            onChange(next);
        }
    };

    const toggleModel = (id) => {
        const next = selected.includes(id)
            ? selected.filter((x) => x !== id)
            : [...selected, id];
        onChange(next);
    };

    const clearAll = (e) => { e.stopPropagation(); onChange([]); };

    // Compute the trigger button label. Counting-first feels cleaner
    // than spelling out source names when the user has many bound.
    const selectedSources = sources.filter((s) => isSourceSelected(s) || isSourcePartial(s));
    const selectedDatasetCount = selected.filter((id) => !id.startsWith("model:")).length;
    const selectedModelCount = selected.filter((id) => id.startsWith("model:")).length;

    let labelText = "No data context";
    if (selectedSources.length === 1 && selectedModelCount === 0) {
        const src = selectedSources[0];
        labelText = isSourcePartial(src)
            ? `${src.label} (partial)`
            : src.label;
    } else if (selectedSources.length > 0 || selectedModelCount > 0) {
        const parts = [];
        if (selectedSources.length) parts.push(`${selectedSources.length} source${selectedSources.length === 1 ? "" : "s"}`);
        if (selectedDatasetCount) parts.push(`${selectedDatasetCount} dataset${selectedDatasetCount === 1 ? "" : "s"}`);
        if (selectedModelCount) parts.push(`${selectedModelCount} model${selectedModelCount === 1 ? "" : "s"}`);
        labelText = parts.join(" · ");
    }

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={`h-9 rounded-md border px-3 text-xs flex items-center gap-1.5 min-w-[200px] max-w-[280px] transition-colors ${
                        selected.length === 0
                            ? "border-amber-400/70 bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 hover:border-amber-500"
                            : "bg-card border-border hover:border-foreground/30"
                    }`}
                    data-testid="sense-ai-dataset-select"
                    data-empty={selected.length === 0 ? "true" : "false"}
                >
                    <Database className={`h-3.5 w-3.5 shrink-0 ${selected.length === 0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`} />
                    <span className="truncate flex-1 text-left">{labelText}</span>
                    {selected.length > 0 && (
                        <span
                            onClick={clearAll}
                            className="h-4 w-4 inline-flex items-center justify-center rounded-sm hover:bg-muted text-muted-foreground"
                            data-testid="sense-ai-dataset-clear"
                            title="Clear all"
                        >
                            <X className="h-3 w-3" />
                        </span>
                    )}
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[340px] p-0" align="end">
                <div className="px-3 py-2 border-b border-border text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    Pick a source — all its datasets are bound automatically
                </div>
                <div className="max-h-[360px] overflow-y-auto">
                    {/* Canonical models — SA-only because the parent only
                        pushes _canonical items in for super_admin. */}
                    {canonical.length > 0 && (
                        <div className="py-2">
                            <div className="px-3 pb-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                                Canonical models
                            </div>
                            {canonical.map((d) => (
                                <label
                                    key={d.id}
                                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/40 cursor-pointer text-xs"
                                    data-testid={`sense-ai-dataset-option-${d.id}`}
                                >
                                    <Checkbox
                                        checked={selected.includes(d.id)}
                                        onCheckedChange={() => toggleModel(d.id)}
                                    />
                                    <span>{d.name}</span>
                                </label>
                            ))}
                        </div>
                    )}
                    {sources.length > 0 && (
                        <div className={`py-2 ${canonical.length > 0 ? "border-t border-border" : ""}`}>
                            <div className="px-3 pb-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                                Sources
                            </div>
                            {sources.map((src) => {
                                const checked = isSourceSelected(src);
                                const partial = isSourcePartial(src);
                                return (
                                    <label
                                        key={src.key}
                                        className="flex items-center gap-2 px-3 py-2 hover:bg-muted/40 cursor-pointer text-xs"
                                        data-testid={`sense-ai-source-option-${src.key}`}
                                    >
                                        <Checkbox
                                            checked={checked}
                                            // Tri-state visual via `data-state="indeterminate"` is
                                            // out of scope — the partial badge below already signals
                                            // mixed selection.
                                            onCheckedChange={() => toggleSource(src)}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="truncate font-medium text-foreground" title={src.label}>
                                                {src.label}
                                            </div>
                                            <div className="text-[10px] text-muted-foreground">
                                                {src.dataset_ids.length} dataset{src.dataset_ids.length === 1 ? "" : "s"}
                                                {partial && " · partial"}
                                                {src.category && <span data-testid={`sense-ai-source-category-${src.key}`}> · {src.category === "file" ? "File dataset" : src.category}</span>}
                                                {src.model && (
                                                    <span data-testid={`sense-ai-source-model-${src.key}`}>
                                                        {" → "}{src.model}{src.basis === "default" ? " (default)" : ""}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                    {canonical.length === 0 && sources.length === 0 && (
                        <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                            No sources or canonical models available yet.
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

const BUILDER_SUGGESTIONS = [
    { icon: TrendingUp, tint: "primary",  text: "Propose a Marketing Performance dashboard bound to the campaign model." },
    { icon: Users,      tint: "emerald",  text: "Suggest 8 synonym candidates for campaign.spend." },
    { icon: Target,     tint: "amber",    text: "Validate the KPI formula SUM(revenue) / COUNT(order_id)." },
    { icon: BarChart3,  tint: "sky",      text: "Match this schema to a canonical data_model: CampaignID, TotalCost, ImpressionCount." },
];
const ASK_SUGGESTIONS = [
    { icon: TrendingUp, tint: "primary",  text: "What's our revenue trend over the last 6 months?" },
    { icon: Users,      tint: "emerald",  text: "Identify top 5 customer segments by LTV." },
    { icon: Target,     tint: "amber",    text: "Which marketing channel has the best ROAS?" },
    { icon: BarChart3,  tint: "sky",      text: "Forecast Q4 pipeline based on current deal stages." },
];

const TINT_CLASSES = {
    primary: { wrap: "border-primary/15 bg-primary/[0.04]",                    icon: "bg-primary/10 text-primary",                                       arrow: "text-primary" },
    emerald: { wrap: "border-emerald-500/15 bg-emerald-500/[0.04]",            icon: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",         arrow: "text-emerald-600 dark:text-emerald-300" },
    amber:   { wrap: "border-amber-500/15 bg-amber-500/[0.04]",                icon: "bg-amber-500/10 text-amber-700 dark:text-amber-300",               arrow: "text-amber-600 dark:text-amber-300" },
    sky:     { wrap: "border-sky-500/15 bg-sky-500/[0.04]",                    icon: "bg-sky-500/10 text-sky-700 dark:text-sky-300",                     arrow: "text-sky-600 dark:text-sky-300" },
};

/* -------------------------------------------------------------------------- */
/*  SuggestionCard — colored prompt-starter card                              */
/* -------------------------------------------------------------------------- */
function SuggestionCard({ suggestion, onPick, testId }) {
    const { icon: Icon, tint, text } = suggestion;
    const cl = TINT_CLASSES[tint] || TINT_CLASSES.primary;
    return (
        <button
            type="button"
            onClick={() => onPick(text)}
            className={`group text-left rounded-lg border p-4 transition-all hover:-translate-y-0.5 hover:shadow-sm ${cl.wrap}`}
            data-testid={testId}>
            <div className={`h-9 w-9 rounded-md flex items-center justify-center mb-3 ${cl.icon}`}>
                <Icon className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <div className="text-[13px] leading-snug text-foreground font-medium pr-4 mb-3">
                {text}
            </div>
            <div className={`text-base ${cl.arrow} opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all`}>
                →
            </div>
        </button>
    );
}

/* -------------------------------------------------------------------------- */
/*  WelcomeHero — centered hero with input + suggestion cards                 */
/* -------------------------------------------------------------------------- */
function WelcomeHero({ ctx }) {
    const { builderMode, isSuperAdmin, setInput, send, input, loading, datasetIds, datasets } = ctx;
    const suggestions = builderMode ? BUILDER_SUGGESTIONS : ASK_SUGGESTIONS;
    // iter-121.31 — Show SOURCE names (not dataset names) in the context
    // hint so end-users see the same label they picked from the dropdown.
    // Datasets are auto-bound per source; surfacing 17 raw dataset names
    // here would just be noise.
    const ctxLabels = (() => {
        if (!datasetIds?.length) return [];
        const seen = new Set();
        const out = [];
        for (const id of datasetIds) {
            if (id.startsWith("model:")) {
                const m = datasets.find((d) => d.id === id);
                if (m && !seen.has(m.name)) { seen.add(m.name); out.push(m.name); }
            } else {
                const d = datasets.find((x) => x.id === id);
                const label = d?.source_name || d?.name || id;
                if (!seen.has(label)) { seen.add(label); out.push(label); }
            }
        }
        return out;
    })();

    const headline = builderMode ? (
        <>Design <span className="font-serif-display italic font-normal text-primary">prebuilts</span> with AI.</>
    ) : (
        <>Ask <span className="font-serif-display italic font-normal text-primary">anything</span> about your data.</>
    );
    const subhead = builderMode
        ? (isSuperAdmin
            ? "Builder mode turns Sense AI into a template-curation workbench. SA confirmations save to Gallery as a prebuilt every tenant auto-clones on data connect."
            : "Builder mode lets you describe a dashboard — the AI proposes a full spec as a tool-call card you can Confirm, Edit, or Reject.")
        : "Sense AI reads your data, finds patterns, and delivers insights instantly.";

    return (
        <div className="w-full px-8 lg:px-12 xl:px-16 pt-8 pb-6" data-testid="sense-ai-welcome">
            <div className="text-center mb-6 max-w-4xl mx-auto">
                <h1 className="text-4xl sm:text-5xl lg:text-[52px] font-semibold tracking-tight leading-[1.1] mb-3">
                    {headline}
                </h1>
                <p className="text-sm text-muted-foreground max-w-xl mx-auto leading-relaxed">
                    {subhead}
                </p>
            </div>

            {/* Hero input — primary-accent border, large, centered. Wider on
                large screens so the prompt area visually anchors the page. */}
            <div className="max-w-5xl mx-auto">
                <PromptInput ctx={ctx} hero />
            </div>

            {ctxLabels.length > 0 && (
                <div className="mt-3 text-center text-[10px] font-mono text-muted-foreground" data-testid="sense-ai-hero-context">
                    Context: {ctxLabels.join(" + ")}
                </div>
            )}

            <div className="mt-8">
                <h2 className="text-sm font-semibold text-foreground mb-4">
                    Try asking {builderMode ? "the builder" : "about your data"}
                </h2>
                {/* iter-121.30 — Suggestion cards span the full available
                    width (not constrained to a 3xl column). Min/max card
                    widths still keep them readable on ultra-wide screens. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="sense-ai-suggestions">
                    {suggestions.map((s, i) => (
                        <SuggestionCard
                            key={`${builderMode ? "b" : "a"}-${i}`}
                            suggestion={s}
                            onPick={(t) => {
                                setInput(t);
                                // Auto-send if the user clicks while idle. Keeps the
                                // one-tap delight UX the design comp implies.
                                if (!loading) setTimeout(() => send && send(), 50);
                            }}
                            testId={`sense-ai-suggestion-${i}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  PromptInput — used both as hero (large, centered) and as the chat footer  */
/*  input (compact). Single source of truth — preserves textarea behavior +   */
/*  Shift+Enter handling from iter-121.22.                                    */
/* -------------------------------------------------------------------------- */
function PromptInput({ ctx, hero = false }) {
    const { input, setInput, send, loading, builderMode, datasetIds, setDataCtxOpen,
            budgetExhausted, clearBudgetExhausted,
            budgetWarn, dismissBudgetWarn,
            attaching, attachFile, hasWrite } = ctx;
    const taRef = useRef(null);
    const fileRef = useRef(null);
    const locked = !!budgetExhausted;
    const isReadOnly = hasWrite === false;
    const isDisabled = locked || isReadOnly;
    const isUploading = !!attaching;
    const onPickFile = () => { if (!isDisabled && !isUploading) fileRef.current?.click(); };
    const onFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        // Reset the input so the same file can be re-picked.
        e.target.value = "";
        for (const f of files) {
            // Sequential — keeps memory low and stacks datasetIds in order.
            // No size pre-check; chunked uploader streams any size.
            // eslint-disable-next-line no-await-in-loop
            await attachFile(f);
        }
    };
    return (
        <div className="relative">
            {/* iter-121.87 — Soft "approaching cap" banner. Surfaces BEFORE
                the hard cap so users see they're getting close. Dismissable
                per-session; reappears on fresh load if usage still over warn. */}
            {!locked && budgetWarn && (
                <div className="mb-3 rounded-lg border border-amber-300/70 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200 px-3 py-2"
                     role="status"
                     data-testid="sense-ai-budget-warn">
                    <div className="flex items-center gap-2.5">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        <div className="text-xs flex-1 min-w-0">
                            <span className="font-semibold">{budgetWarn.pct}% of your AI credits used</span>
                            <span className="opacity-80"> · {budgetWarn.used.toLocaleString()}/{budgetWarn.cap.toLocaleString()} credits this month. Chat will pause at {Math.round(budgetWarn.warnAt < 100 ? 100 : budgetWarn.warnAt)}%.</span>
                        </div>
                        <button
                            type="button"
                            onClick={dismissBudgetWarn}
                            className="h-6 w-6 inline-flex items-center justify-center rounded-md hover:bg-amber-200/50 dark:hover:bg-amber-500/20 shrink-0"
                            title="Dismiss"
                            data-testid="sense-ai-budget-warn-dismiss">
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            )}
            {/* iter-121.86 — Locked banner shown ABOVE the input when the
                user's personal cap (or the tenant cap) is hit. Replaces the
                old "Continue with cheaper model" dialog per user ask:
                the chat should JUST STOP until budget is restored.
                "Try again" rehits the backend gate — if the cap was raised
                (or a new month started), the chat unlocks automatically. */}
            {locked && (
                <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200 px-4 py-3"
                     role="alert"
                     data-testid="sense-ai-budget-locked">
                    <div className="flex items-start gap-3">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-200/60 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200">
                            <ShieldQuestion className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold">
                                {budgetExhausted.scope === "tenant"
                                    ? "Organisation AI credit budget exhausted"
                                    : "Personal AI credit cap reached"}
                            </div>
                            <div className="text-xs mt-1 leading-relaxed">
                                {budgetExhausted.message}
                                {" "}
                                Chat is paused until your monthly allotment renews
                                {budgetExhausted.scope === "personal" ? " or an admin raises your cap." : "."}
                            </div>
                            <button
                                type="button"
                                onClick={clearBudgetExhausted}
                                className="mt-2 inline-flex items-center text-[11px] font-medium underline-offset-2 hover:underline"
                                data-testid="sense-ai-budget-retry">
                                Try again
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* iter-121.87 — Upload progress strip above input while a
                file is being chunk-uploaded + ingested. Non-blocking — the
                user can still type or send other prompts. */}
            {isUploading && (
                <div className="mb-3 rounded-lg border border-primary/30 bg-primary/[0.04] text-foreground px-3 py-2"
                     role="status"
                     data-testid="sense-ai-attach-progress">
                    <div className="flex items-center gap-2.5">
                        <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate">
                                Attaching <span className="font-mono">{attaching.name}</span>
                            </div>
                            <div className="mt-1 h-1 w-full rounded-full bg-primary/10 overflow-hidden">
                                <div
                                    className="h-full bg-primary transition-all"
                                    style={{ width: `${Math.min(100, Math.max(2, attaching.pct || 0))}%` }}
                                    data-testid="sense-ai-attach-progress-bar"
                                />
                            </div>
                            <div className="mt-1 text-[10px] text-muted-foreground">
                                {attaching.stage || "Processing"} · {attaching.pct || 0}%
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {isReadOnly && (
                <div className="mb-3 rounded-lg border border-primary/30 bg-primary/5 text-foreground px-4 py-3" role="alert">
                    <div className="flex items-start gap-3">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/20 text-primary">
                            <ShieldQuestion className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold">Read-only mode</div>
                            <div className="text-xs mt-1 leading-relaxed">
                                You have View-only permissions for Sense AI. You can view past conversations but cannot ask new questions or create sessions.
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {!builderMode && !datasetIds?.length && !isDisabled && (
                <button
                    type="button"
                    onClick={() => setDataCtxOpen?.(true)}
                    className="w-full mb-2 flex items-center gap-2 rounded-lg border border-amber-400/60 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-left text-xs text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-500/15 transition-colors"
                    data-testid="sense-ai-no-dataset-nudge"
                >
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span><strong className="font-semibold">No dataset connected</strong> — pick or upload one so I can analyze your real numbers.</span>
                </button>
            )}
            <div className={`relative rounded-xl border bg-card transition-shadow ${
                hero ? "border-primary/30 shadow-sm" : "border-border"
            } ${isDisabled  ? "opacity-60 pointer-events-none" : "focus-within:ring-2 focus-within:ring-primary/25 focus-within:border-primary/40"}`}>
                <textarea
                    ref={taRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (!isDisabled) send(); } }}
                     placeholder={isDisabled ? (isReadOnly ? "Chat is disabled in Read-only mode." : "Chat is paused — budget cap reached.") : (builderMode ? "Describe the dashboard, formula or data model to build…" : "Ask a question about your data…")}
                    rows={hero ? 3 : 2}
                    disabled={isDisabled}
                    className="w-full resize-none bg-transparent text-sm focus:outline-none p-4 pb-12 disabled:cursor-not-allowed"
                    data-testid="sense-ai-input"
                />
                {/* Bottom action row */}
                <div className="absolute left-3 bottom-3 flex items-center gap-1.5">
                    {/* iter-121.87 — hidden file input driven by the
                        paperclip button. Accepts any tabular file the
                        backend supports; size is unbounded client-side
                        (chunked uploader streams it). */}
                    <input
                        ref={fileRef}
                        type="file"
                        multiple
                        accept=".csv,.xlsx,.xls,.json,.tsv,.txt,text/csv,application/json"
                        onChange={onFileChange}
                        className="hidden"
                        data-testid="sense-ai-attach-input"
                    />
                    <button
                        type="button"
                        className={`h-7 w-7 inline-flex items-center justify-center rounded-md transition-colors ${
                            isUploading
                                ? "bg-primary/15 text-primary cursor-wait"
                                : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                        }`}
                        title={isUploading ? `Uploading ${attaching?.name}…` : "Attach a CSV/XLSX/JSON file — Sense AI will read it and let you ask questions about its data"}
                        data-testid="sense-ai-attach"
                        disabled={isDisabled || isUploading}
                        onClick={onPickFile}>
                        {isUploading
                            ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                            : <Paperclip className="h-4 w-4" strokeWidth={1.75} />}
                    </button>
                </div>
                <div className="absolute right-3 bottom-3 flex items-center gap-3">
                    <span className="text-[10px] text-muted-foreground hidden sm:inline">
                        Shift + Enter for new line
                    </span>
                    <Button
                        size="icon"
                        onClick={send}
                        disabled={loading || !input.trim() || isDisabled}
                        className="h-8 w-8 rounded-md shrink-0"
                        data-testid="sense-ai-send"
                        title={isDisabled ? (isReadOnly ? "Chat is disabled" : "Budget cap reached — chat is paused") : "Send (Enter)"}>
                        <Send className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

const MARKDOWN_COMPONENTS = {
    p: ({ node, ...p }) => <p {...p} className="mb-2 last:mb-0" />,
    strong: ({ node, ...p }) => <strong {...p} className="font-semibold text-foreground" />,
    em: ({ node, ...p }) => <em {...p} className="italic" />,
    ul: ({ node, ...p }) => <ul {...p} className="list-disc pl-5 mb-2 space-y-1" />,
    ol: ({ node, ...p }) => <ol {...p} className="list-decimal pl-5 mb-2 space-y-1" />,
    li: ({ node, ...p }) => <li {...p} className="leading-relaxed" />,
    code: ({ node, inline, ...p }) => inline
        ? <code {...p} className="px-1 py-0.5 bg-secondary rounded-sm font-mono text-[13px]" />
        : <code {...p} className="block p-3 bg-secondary rounded-sm font-mono text-[12px] overflow-x-auto" />,
    h3: ({ node, ...p }) => <h3 {...p} className="text-base font-semibold mt-3 mb-1.5" />,
    h4: ({ node, ...p }) => <h4 {...p} className="text-sm font-semibold mt-2 mb-1" />,
    a: ({ node, ...p }) => <a {...p} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer" />,
};

/* -------------------------------------------------------------------------- */
/*  LoadingIndicator — Gemini-style loading wave + cycling status texts       */
/* -------------------------------------------------------------------------- */
function LoadingIndicator() {
    const [statusIndex, setStatusIndex] = React.useState(0);
    const statuses = [
        "Sense AI is thinking...",
        "Analyzing the data...",
        "Creating visuals..."
    ];

    React.useEffect(() => {
        const interval = setInterval(() => {
            setStatusIndex((prev) => (prev + 1) % statuses.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="border-l-2 border-primary pl-5 py-1" data-testid="sense-ai-loading">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-primary mb-2 flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" /> Sense AI
            </div>
            <div className="flex items-center gap-3">
                {/* Replaced 3-dot response generation loader animation with spiral collapse animation following selected theme colors */}
                <div className="spiral" data-testid="sense-ai-spiral-loader">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
                <span className="text-xs text-muted-foreground animate-pulse font-medium">
                    {statuses[statusIndex]}
                </span>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  MessagesList — preserved layout, identical message structure              */
/* -------------------------------------------------------------------------- */
const isRefusalOrOutofDomain = (m) => {
    if (!m || !m.content) return false;
    const text = m.content.toLowerCase();
    return (
        text.includes("outside what i can help with") ||
        text.includes("please rephrase") ||
        text.includes("rephrase it") ||
        text.includes("rephrase the question") ||
        text.includes("rephrase as a")
    );
};

function MessagesList({ ctx }) {
    const navigate = useNavigate();
    const { messages, loading, bottomRef, saveToGallery, saveKpiToGallery, saveChartToGallery, savedKeys, galleryKeysMap } = ctx;
    return (
        <div className="w-full px-8 lg:px-12 xl:px-16 py-8" data-testid="sense-ai-messages-inner">
            <div className="max-w-5xl mx-auto space-y-6">
            {messages.map((m) => {
                const insightKey = `insight:${m.id}`;
                const insightSaved = savedKeys?.has?.(insightKey);
                return (
                <div key={m.id} className="fadein" data-testid={`sense-ai-message-${m.id}`}>
                    {m.role === "user" ? (
                        <div className="flex justify-end">
                            <div className="bg-secondary rounded-lg p-4 text-sm max-w-[80%]">
                                {m.content}
                            </div>
                        </div>
                    ) : (
                        <div className="border-l-2 border-primary pl-5">
                            <div className="text-[10px] uppercase tracking-wider font-semibold text-primary mb-2 flex items-center gap-1.5">
                                <Sparkles className="h-3 w-3" /> Sense AI {/* {m.generation_time && <span className="normal-case text-muted-foreground/70 font-normal ml-1" data-testid={`sense-ai-time-${m.id}`}>({m.generation_time}s)</span>} */}
                            </div>
                            <div className="text-[15px] leading-relaxed senseai-markdown">
                                <ReactMarkdown
                                    disallowedElements={["script", "iframe", "img", "video", "audio", "object", "embed", "svg", "style"]}
                                    unwrapDisallowed
                                    components={MARKDOWN_COMPONENTS}>
                                    {stripStructured(m.content)}
                                </ReactMarkdown>
                            </div>
                            {m.structured && <StructuredView s={m.structured} messageId={m.id} savedKeys={savedKeys} galleryKeysMap={galleryKeysMap} onSaveKpi={saveKpiToGallery} onSaveChart={saveChartToGallery} onFollowupClick={(text) => ctx.setInput?.(text)} />}
                            {Array.isArray(m.tool_calls) && m.tool_calls.map((call, i) => (
                                <BuilderToolCard
                                    key={`${m.id}-tc-${i}`}
                                    call={call}
                                    result={(m.tool_results || [])[i]}
                                    siblings={m.tool_calls}
                                    siblingResults={m.tool_results || []}
                                    onRerun={() => {}}
                                />
                            ))}
                            {!isRefusalOrOutofDomain(m) && (
                                <div className="mt-3 flex items-center gap-2">
                                    {insightSaved ? (
                                        galleryKeysMap?.get(insightKey) ? (
                                            <Button variant="outline" size="sm"
                                                    className="h-7 rounded-md text-xs border-[hsl(var(--success))] text-[hsl(var(--success))] hover:text-primary hover:border-primary shrink-0"
                                                    onClick={() => navigate(`/gallery?preview=${galleryKeysMap.get(insightKey)}`)}
                                                    title="Open this insight in the Gallery"
                                                    data-testid={`sense-ai-open-${m.id}`}>
                                                <BookmarkCheck className="h-3.5 w-3.5 mr-1" /> Open in Gallery
                                            </Button>
                                        ) : (
                                            <span className="text-xs text-[hsl(var(--success))] font-medium flex items-center gap-1">
                                                <BookmarkCheck className="h-3.5 w-3.5" /> Saved to Gallery
                                            </span>
                                        )
                                    ) : (
                                        <Button variant="outline" size="sm"
                                                className="h-7 rounded-md text-xs"
                                                onClick={() => saveToGallery(m.content, m.structured, insightKey)}
                                                title="Save this insight to Gallery"
                                                data-testid={`sense-ai-save-${m.id}`}>
                                            <BookmarkPlus className="h-3.5 w-3.5 mr-1" /> Save insight to Gallery
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    ) }
                </div>
                );
            })}
            {loading && <LoadingIndicator />}
            <div ref={bottomRef} />
            </div>
        </div>
    );
}


/* -------------------------------------------------------------------------- */
/*  ChatHeader — refined header bar                                           */
/* -------------------------------------------------------------------------- */
function ChatHeader({ ctx }) {
    const {
        active, builderMode, setBuilderMode, isSuperAdmin, setStructuredOpen,
        datasetIds, setDatasetIds, datasets, provider, setProvider, providers,
        dataCtxOpen, setDataCtxOpen,
    } = ctx;
    const { entitled, openUpgrade } = useEntitlements();
    // Plan gate — Sense AI Builder is Enterprise-only. SA always allowed.
    const builderLocked = !isSuperAdmin && !entitled("sense_ai_builder");
    return (
        <div className="h-[64px] border-b border-border px-6 flex items-center justify-between shrink-0 bg-background/80 backdrop-blur-sm" data-testid="sense-ai-header">
            <div className="flex items-center gap-2.5 min-w-0">
                <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                    <Sparkles className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate" data-testid="sense-ai-title">
                        {active?.title || "Sense AI"}
                    </div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {builderMode ? "Builder mode · template curation" : "Natural language analytics"}
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {builderLocked ? (
                    <button
                        onClick={() => openUpgrade()}
                        className="h-9 rounded-md border bg-card border-border hover:border-foreground/30 px-3 text-xs flex items-center gap-1.5 transition-colors"
                        data-testid="sense-ai-builder-toggle"
                        title="Sense AI Builder isn't included in your plan — upgrade to unlock">
                        <Lock className="h-3.5 w-3.5" />
                        Builder
                        <span className="ml-1 text-[9px] font-mono uppercase rounded bg-primary/10 text-primary px-1 py-0.5">Upgrade</span>
                    </button>
                ) : (
                <button
                    onClick={() => setBuilderMode((v) => !v)}
                    className={`h-9 rounded-md border px-3 text-xs flex items-center gap-1.5 transition-colors ${
                        builderMode
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-card border-border hover:border-foreground/30"
                    }`}
                    data-testid="sense-ai-builder-toggle"
                    title={builderMode ? "Switch back to Ask mode" : "Switch to Builder mode"}>
                    <Wrench className="h-3.5 w-3.5" />
                    Builder{builderMode ? " · ON" : ""}
                </button>
                )}
                {builderMode && isSuperAdmin && (
                    <button
                        onClick={() => setStructuredOpen(true)}
                        className="h-9 rounded-md border bg-card border-border hover:border-foreground/30 px-3 text-xs flex items-center gap-1.5 transition-colors"
                        data-testid="sense-ai-structured-builder"
                        title="Open the structured form-driven template builder">
                        <Pencil className="h-3.5 w-3.5" />
                        Structured
                    </button>
                )}
                {builderMode && isSuperAdmin && (
                    <ApplyThemeToolbarButton />
                )}
                <DataContextSelect
                    datasets={datasets}
                    value={datasetIds}
                    onChange={setDatasetIds}
                    open={dataCtxOpen}
                    onOpenChange={setDataCtxOpen}
                />
                <Select value={provider} onValueChange={setProvider} disabled={providers.length === 0}>
                    <SelectTrigger className="h-9 rounded-md w-[240px] text-xs" data-testid="sense-ai-provider-select">
                        <ProviderTriggerValue providers={providers} value={provider} disabled={providers.length === 0} />
                    </SelectTrigger>
                    <SelectContent>
                        {providers.length === 0 ? (
                            <div className="px-3 py-2 text-[11px] text-muted-foreground" data-testid="sense-ai-no-providers">
                                No LLM provider is both enabled and configured.
                                <div className="mt-1">Go to <b>Settings → LLM Providers</b>.</div>
                            </div>
                        ) : providers.map((p) => (
                            <SelectItem key={p.value} value={p.value} data-testid={`sense-ai-provider-option-${p.provider}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="truncate">{p.label}</span>
                                    {p.source === "platform_default" && (
                                        <span
                                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-[9px] font-mono uppercase tracking-wide shrink-0"
                                            title="Using the platform default key configured by the Super Admin. Configure your own key in Settings → AI Providers for billing isolation."
                                            data-testid={`sense-ai-provider-option-${p.provider}-platform-default`}>
                                            <ShieldQuestion className="h-2.5 w-2.5" /> Platform default
                                        </span>
                                    )}
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  PrivacyFooter — bottom banner                                             */
/* -------------------------------------------------------------------------- */
function PrivacyFooter() {
    return (
        <div className="border-t border-border bg-primary/[0.03] px-6 py-2.5 flex items-center justify-between text-[11px] text-muted-foreground" data-testid="sense-ai-privacy-footer">
            <div className="flex items-center gap-2">
                <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                <span>Your data is private and secure. Sense AI only uses the data you have access to.</span>
            </div>
            <a
                href="https://docs.analyzio.io/sense-ai/data-privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
                data-testid="sense-ai-privacy-learn-more">
                Learn more <ExternalLink className="h-3 w-3" />
            </a>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  ChatPane — top-level shell                                                */
/* -------------------------------------------------------------------------- */
export default function ChatPane({ ctx }) {
    const { messages } = ctx;
    const hasMessages = (messages || []).length > 0;
    // iter-121.31b — Single source of truth for the data-context popover so
    // BOTH the header chip and the input-area database shortcut open the
    // same picker. The shortcut previously only flashed the header without
    // actually triggering the popover.
    const [dataCtxOpen, setDataCtxOpen] = useState(false);
    const enhancedCtx = { ...ctx, dataCtxOpen, setDataCtxOpen };
    return (
        <div className="flex-1 flex flex-col min-w-0 relative" data-testid="sense-ai-chat-pane">
            <ChatHeader ctx={enhancedCtx} />
            <div className="flex-1 overflow-y-auto scrollbar-thin" data-testid="sense-ai-messages">
                {hasMessages ? <MessagesList ctx={enhancedCtx} /> : <WelcomeHero ctx={enhancedCtx} />}
            </div>
            {/* Chat footer input only renders once a conversation exists.
                The welcome screen owns its own large hero input — having
                two inputs simultaneously is what created the old
                "what does what?" UX. */}
            {hasMessages && (
                <div className="border-t border-border px-6 lg:px-12 py-4 shrink-0" data-testid="sense-ai-input-footer">
                    <div className="max-w-5xl mx-auto">
                        <PromptInput ctx={enhancedCtx} />
                    </div>
                </div>
            )}
            <PrivacyFooter />
        </div>
    );
}